import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, Tag, Space, message,
         Card, Row, Col, Switch, DatePicker, Popconfirm, Tabs, Statistic,
         Divider, Badge, Tooltip, Progress, Alert } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, BarChartOutlined,
         SendOutlined, CloseCircleOutlined, DragOutlined, CopyOutlined,
         EyeOutlined, TeamOutlined } from '@ant-design/icons';
import SurveyService from '../../services/SurveyService';
import QoCSurveyCollectors from './QoCSurveyCollectors';
import QoCSurveyCollaborators from './QoCSurveyCollaborators';
import QoCSurveyImport    from './QoCSurveyImport';
import QoCSurveyProgress   from './QoCSurveyProgress';
import util from '../../utils/util';
import moment from 'moment';

const { Option } = Select;
const { TextArea } = Input;

const QUESTION_TYPES = [
    { value:'text',      label:'Short Text',       icon:'📝' },
    { value:'textarea',  label:'Long Text',         icon:'📄' },
    { value:'number',    label:'Number',            icon:'🔢' },
    { value:'single',    label:'Single Choice',     icon:'🔘' },
    { value:'multiple',  label:'Multiple Choice',   icon:'☑️' },
    { value:'dropdown',  label:'Dropdown (Select)',  icon:'🔽' },
    { value:'matrix',    label:'Matrix / Grid',       icon:'📊' },
    { value:'scale',     label:'Rating Scale',      icon:'⭐' },
    { value:'boolean',   label:'Yes / No',          icon:'✅' },
    { value:'date',      label:'Date',              icon:'📅' },
];

const STATUS_COLORS = { draft:'default', published:'green', closed:'red', archived:'grey' };

export default function QoCSurveyBuilder() {
    const [surveys,      setSurveys]      = useState([]);
    const [programs,     setPrograms]     = useState([]);
    const [facilities,   setFacilities]   = useState([]);
    const [filterProg,   setFilterProg]   = useState(null);
    const [loading,      setLoading]      = useState(false);
    // Permission checks
    const canAdd    = util.canAdd('survey_admin');
    const canEdit   = util.canEdit('survey_admin');
    const canDelete = util.canDelete('survey_admin');
    const canExport = util.canExport('survey_admin');
    const isProgramUser = util.isProgramUser?.() || false;
    const userProgramId = util.getProgramId?.() || null;
    const currentUserId = parseInt(util.getUserId?.() || '0');
    const isGeneralUser = util.getAccountType?.() === 'general';
    const [surveyModal,  setSurveyModal]  = useState(false);
    const [builderModal, setBuilderModal] = useState(false);
    const [analyticsModal, setAnalyticsModal] = useState(false);
    const [linkModal,      setLinkModal]    = useState(false);
    const [collectorsModal, setCollectorsModal] = useState(false);
    const [progressModal,   setProgressModal]   = useState(false);
    const [importModal,     setImportModal]     = useState(false);
    const [collabModal,     setCollabModal]     = useState(false);
    const [publicLink,   setPublicLink]   = useState('');
    const [current,      setCurrent]      = useState(null);
    const [analytics,    setAnalytics]    = useState(null);
    const [targetType,   setTargetType]   = useState('all');
    const [form]  = Form.useForm();

    // Builder state
    const [sections,  setSections]  = useState([{ id:'s1', title:'Section 1', description:'' }]);
    const [questions, setQuestions] = useState([]);
    const [activeTab, setActiveTab] = useState('questions');

    useEffect(() => {
        load();
        import('../../services/ProgramsService').then(({ default: ProgramsService }) => {
            ProgramsService.getPrograms().then(r => setPrograms(r.data?.data || []));
        });
        import('../../services/FacilityService').then(({ default: FacilityService }) => {
            // FacilityService returns clients rows: { id, business_name, located_region, site_type }
            FacilityService.all({ status: 1 }).then(r => {
                const rows = r.data?.result?.data || r.data?.data || r.data?.result || [];
                setFacilities(rows.map(f => ({
                    id:        f.id,
                    name:      f.business_name || f.name,
                    region:    f.located_region || f.region || '',
                    site_type: f.site_type || '',
                })));
            }).catch(() => {});
        });
    }, []);

    // For general/collaborator users, build program list from their co-owned surveys
    useEffect(() => {
        if (isGeneralUser && surveys.length > 0) {
            const coOwnedPrograms = surveys
                .filter(s => s.collaborator_role === 'co_owner' && s.program_id)
                .map(s => ({ id: s.program_id, name: s.program_name }))
                .filter((p, i, arr) => arr.findIndex(x => x.id === p.id) === i);
            if (coOwnedPrograms.length > 0) setPrograms(coOwnedPrograms);
        }
    }, [surveys, isGeneralUser]);

    const load = () => {
        setLoading(true);
        SurveyService.getSurveys()
            .then(r => setSurveys(r.data?.data || []))
            .catch(() => {})
            .finally(() => setLoading(false));
    };

    const openNew = () => {
        form.resetFields();
        setTargetType('all');
        if (isProgramUser && userProgramId) {
            form.setFieldsValue({ program_id: parseInt(userProgramId) });
        } else if (isGeneralUser) {
            // Pre-fill program from a co_owned survey
            const coOwnedSurvey = surveys.find(s => s.collaborator_role === 'co_owner');
            if (coOwnedSurvey?.program_id) {
                form.setFieldsValue({ program_id: parseInt(coOwnedSurvey.program_id) });
            }
        }
        setCurrent(null);
        setSurveyModal(true);
    };

    const openEdit = (s) => {
        form.setFieldsValue({
            ...s,
            period: s.period_start ? [moment(s.period_start), moment(s.period_end)] : null,
        });
        setTargetType(s.target_type || 'all');
        setCurrent(s);
        setSurveyModal(true);
    };

    const saveSurvey = async () => {
        try {
            const vals = await form.validateFields();
            util.showLoader();
            const payload = {
                ...vals,
                id:           current?.id,
                period_start: vals.period?.[0]?.format('YYYY-MM-DD'),
                period_end:   vals.period?.[1]?.format('YYYY-MM-DD'),
            };
            delete payload.period;
            const res = await SurveyService.saveSurvey(payload);
            message.success('Survey saved');
            setSurveyModal(false);
            load();
            if (!current) {
                // Open builder for new survey
                const detail = await SurveyService.getSurveyDetail(res.data?.data?.id);
                openBuilder(detail.data?.data);
            }
        } catch(e) { message.error(e.message || 'Save failed'); }
        finally { util.hideLoader(); }
    };

    const openBuilder = async (s) => {
        if (s.status === 'published') {
            if (!window.confirm('This survey is published and may have active collectors. Editing questions could affect existing responses. Continue?')) return;
        }
        util.showLoader();
        try {
            const res = await SurveyService.getSurveyDetail(s.id);
            const detail = res.data?.data;
            setCurrent(detail);
            setSections(detail.sections?.length ? detail.sections : [{ id:'s1', title:'Section 1', description:'' }]);
            setQuestions((detail.questions || []).map(q => { const parseArr = v => typeof v === 'string' ? (() => { try { return JSON.parse(v); } catch(e) { return []; } })() : (Array.isArray(v) ? v : []); const t = q.type && q.type !== '' ? q.type : 'text'; const sl = q.skip_logic ? (typeof q.skip_logic==='string' ? (() => { try { return JSON.parse(q.skip_logic); } catch(e) { return null; } })() : q.skip_logic) : null; return {...q, type: t, options: parseArr(q.options), rows: parseArr(q.rows), columns: parseArr(q.columns).length ? parseArr(q.columns) : ['Yes','No'], skip_logic: sl, xlsform_name: q.xlsform_name || String(q.id)}; }));
            setActiveTab('questions');
            setBuilderModal(true);
        } catch(e) { message.error(e.message); }
        finally { util.hideLoader(); }
    };

    const saveQuestions = async () => {
        util.showLoader();
        try {
            console.log('SAVING QUESTIONS:', JSON.stringify(questions.map(q=>({type:q.type,label:q.label})))); await SurveyService.saveQuestions(current.id, { sections, questions });
            message.success('Questions saved');
            setBuilderModal(false);
            load();
        } catch(e) { message.error(e.message); }
        finally { util.hideLoader(); }
    };

    const addQuestion = (sectionId) => {
        const newQ = {
            id:          'q_' + Date.now(),
            section_id:  sectionId,
            type:        'text',
            label:       '',
            required:    true,
            options:     [],
            scale_min:   1,
            scale_max:   5,
            helper_text: '',
            skip_logic:  null,
            rows:        [],
            columns:     ['Yes','No'],
        };
        setQuestions(qs => [...qs, newQ]);
    };

    const updateQuestion = (qid, field, value) => {
        setQuestions(qs => qs.map(q => q.id === qid ? { ...q, [field]: value } : q));
    };

    const removeQuestion = (qid) => {
        setQuestions(qs => qs.filter(q => q.id !== qid));
    };

    const addOption = (qid) => {
        setQuestions(qs => qs.map(q => q.id === qid
            ? { ...q, options: [...(q.options||[]), 'Option ' + ((q.options||[]).length+1)] }
            : q
        ));
    };

    const updateOption = (qid, idx, val) => {
        setQuestions(qs => qs.map(q => {
            if (q.id !== qid) return q;
            const opts = [...(q.options||[])];
            opts[idx] = val;
            return { ...q, options: opts };
        }));
    };

    const removeOption = (qid, idx) => {
        setQuestions(qs => qs.map(q => q.id !== qid ? q : {
            ...q, options: q.options.filter((_, i) => i !== idx)
        }));
    };

    const generateLink = async (s) => {
        util.showLoader();
        try {
            if (s.public_token) {
                // Already has a link — just show it
                const baseUrl = window.location.origin + (window.location.hostname.includes('qoctesting') ? '/qoctesting' : '/qoc');
                setPublicLink(`${baseUrl}/survey/public/${s.public_token}`);
                setCurrent(s);
                setLinkModal(true);
            } else {
                const res = await SurveyService.generatePublicLink(s.id);
                const token = res.data?.data?.token;
                const baseUrl = window.location.origin + (window.location.hostname.includes('qoctesting') ? '/qoctesting' : '/qoc');
                const link = `${baseUrl}/survey/public/${token}`;
                setPublicLink(link);
                setCurrent({...s, public_token: token});
                setLinkModal(true);
                load();
                message.success('Public link generated');
            }
        } catch(e) { message.error(e.message); }
        finally { util.hideLoader(); }
    };

    const revokeLink = async () => {
        util.showLoader();
        try {
            await SurveyService.revokePublicLink(current.id);
            setLinkModal(false);
            setPublicLink('');
            load();
            message.success('Public link revoked');
        } catch(e) { message.error(e.message); }
        finally { util.hideLoader(); }
    };

    const copyLink = () => {
        navigator.clipboard.writeText(publicLink);
        message.success('Link copied to clipboard');
    };

    const openAnalytics = async (s) => {
        util.showLoader();
        try {
            const res = await SurveyService.getAnalytics(s.id);
            setAnalytics(res.data?.data);
            setCurrent(s);
            setAnalyticsModal(true);
        } catch(e) { message.error(e.message); }
        finally { util.hideLoader(); }
    };

    const publish = async (id) => {
        util.showLoader();
        try {
            await SurveyService.publishSurvey(id);
            message.success('Survey published');
            load();
        } catch(e) { message.error(e.message); }
        finally { util.hideLoader(); }
    };

    const close = async (id) => {
        util.showLoader();
        try {
            await SurveyService.closeSurvey(id);
            message.success('Survey closed');
            load();
        } catch(e) { message.error(e.message); }
        finally { util.hideLoader(); }
    };

    const cols = [
        { title:'Survey', dataIndex:'title', render:(t,r)=>(
            <div>
                <div style={{fontWeight:700}}>{t}</div>
                <div style={{fontSize:11,color:'#888'}}>
                    {r.program_name && <Tag color="blue" style={{fontSize:10,marginRight:4}}>{r.program_name}</Tag>}
                    {r.collaborator_role && <Tag color={r.collaborator_role==='co_owner'?'gold':r.collaborator_role==='editor'?'blue':r.collaborator_role==='collector_manager'?'purple':'green'} style={{fontSize:10,marginRight:4}}>{r.collaborator_role.replace('_',' ')}</Tag>}
                    {r.category||'General'} · {r.question_count||0} questions
                </div>
            </div>
        )},
        { title:'Status', dataIndex:'status', width:110,
            render: v => <Tag color={STATUS_COLORS[v]}>{v}</Tag>
        },
        { title:'Responses', dataIndex:'response_count', width:100,
            render: v => <span style={{fontWeight:700,color:'#1890ff'}}>{v||0}</span>
        },
        { title:'Period', width:200, render:(_, r) => r.period_start
            ? `${r.period_start} → ${r.period_end||'Open'}`
            : <span style={{color:'#ccc'}}>No period set</span>
        },
        { title:'', width:220, render:(_, r) => (
            <Space size={4}>
                <Tooltip title="Build Questions">
                    <Button size="small" icon={<EditOutlined/>} onClick={()=>openBuilder(r)} />
                </Tooltip>
                <Tooltip title="Analytics">
                    <Button size="small" icon={<BarChartOutlined/>} onClick={()=>openAnalytics(r)} />
                </Tooltip>
                <Tooltip title="Manage Collectors">
                    <Button size="small" icon={<TeamOutlined/>}
                        onClick={()=>{setCurrent(r);setCollectorsModal(true);}} />
                </Tooltip>
                <Tooltip title="Collaborators & XLSForm">
                    <Button size="small" icon={<span>👥</span>}
                        onClick={()=>{setCurrent(r);setCollabModal(true);}} />
                </Tooltip>
                <Tooltip title="Collection Progress">
                    <Button size="small" icon={<span>📊</span>}
                        onClick={()=>{setCurrent(r);setProgressModal(true);}} />
                </Tooltip>
                <Tooltip title="Export to Excel/CSV">
                    <Button size="small" icon={<span>⬇️</span>}
                        onClick={async ()=>{
                            try {
                                const res = await SurveyService.exportResponses(r.id);
                                const blob = new Blob([res.data], {type:'text/csv;charset=utf-8;'});
                                const url  = URL.createObjectURL(blob);
                                const a    = document.createElement('a');
                                a.href     = url;
                                a.download = `survey_${r.id}_responses.csv`;
                                a.click();
                                URL.revokeObjectURL(url);
                            } catch(e) { message.error('Export failed: ' + e.message); }
                        }} />
                </Tooltip>
                <Tooltip title={r.public_token ? "Public Link (Active)" : "Generate Public Link"}>
                    <Button size="small" icon={<span>🔗</span>}
                        type={r.public_token ? "primary" : "default"}
                        onClick={()=>generateLink(r)} />
                </Tooltip>
                {r.status==='draft' && (
                    <Popconfirm title="Publish this survey?" onConfirm={()=>publish(r.id)} okText="Publish">
                        <Button size="small" type="primary" icon={<SendOutlined/>}>Publish</Button>
                    </Popconfirm>
                )}
                {r.status==='published' && (
                    <Popconfirm title="Close this survey?" onConfirm={()=>close(r.id)} okText="Close" okButtonProps={{danger:true}}>
                        <Button size="small" danger icon={<CloseCircleOutlined/>}>Close</Button>
                    </Popconfirm>
                )}
                {(canEdit || r.collaborator_role === 'co_owner' || r.collaborator_role === 'editor') && <Button size="small" icon={<CopyOutlined/>} onClick={()=>openEdit(r)}>Edit</Button>}
                {(canDelete || r.collaborator_role === 'co_owner') && Number(r.created_by) !== currentUserId && <Popconfirm title="Delete this survey?" onConfirm={async()=>{ await SurveyService.deleteSurvey(r.id); load(); }} okText="Delete" okButtonProps={{danger:true}}><Button size="small" danger icon={<DeleteOutlined/>}/></Popconfirm>}
                {(canDelete || Number(r.created_by) === currentUserId) && <Popconfirm title="Delete this survey?" onConfirm={async()=>{ await SurveyService.deleteSurvey(r.id); load(); }} okText="Delete" okButtonProps={{danger:true}}><Button size="small" danger icon={<DeleteOutlined/>}/></Popconfirm>}
                {Number(r.created_by) === currentUserId && (!r.visibility_status || r.visibility_status === 'private') && (
                    <Button size="small" icon={<SendOutlined/>} onClick={async()=>{ await SurveyService.submitForReview(r.id); message.success('Submitted for national review'); load(); }}>
                        Submit for Review
                    </Button>
                )}
                {r.visibility_status === 'pending_review' && (
                    <Tag color="gold">Pending National Review</Tag>
                )}
                {r.visibility_status === 'public' && (
                    <Tag color="green">Published as Template</Tag>
                )}
                {r.promoted_to_id && (!r.visibility_status || r.visibility_status === 'private') && (
                    <Tooltip title="This tool was approved and published to the National Template Library">
                        <Tag color="blue">✓ Promoted</Tag>
                    </Tooltip>
                )}
            </Space>
        )},
    ];

    return (
        <div className="page-content">
            <div className="page-head d-flex">
                <div className="my-auto">
                    <h4 style={{margin:0}}>Survey Builder</h4>
                    <small className="text-muted">Build, publish and analyse surveys across MoH facilities</small>
                </div>
                <div className="my-auto ml-auto">
                    {(canAdd && !isGeneralUser) || surveys.some(s=>s.collaborator_role==='co_owner') ? <Button icon={<span>📥</span>} onClick={()=>setImportModal(true)} style={{marginRight:8}}>Import XLSForm</Button> : null}
                    {(canAdd && !isGeneralUser) || surveys.some(s=>s.collaborator_role==='co_owner') ? <Button type="primary" icon={<PlusOutlined/>} onClick={openNew}>New Survey</Button> : null}
                </div>
            </div>

            <div className="page-pad">
                {/* Summary */}
                <Row gutter={12} style={{marginBottom:20}}>
                    {[
                        {label:'Total Surveys', value:surveys.length, color:'#1890ff'},
                        {label:'Published', value:surveys.filter(s=>s.status==='published').length, color:'#16a34a'},
                        {label:'Total Responses', value:surveys.reduce((s,x)=>s+parseInt(x.response_count||0),0), color:'#7c3aed'},
                        {label:'Drafts', value:surveys.filter(s=>s.status==='draft').length, color:'#d97706'},
                    ].map(k=>(
                        <Col key={k.label} span={6}>
                            <Card size="small">
                                <Statistic title={k.label} value={k.value}
                                    valueStyle={{color:k.color,fontWeight:800,fontSize:28}}/>
                            </Card>
                        </Col>
                    ))}
                </Row>

                {/* Program filter */}
                {!isProgramUser && (
                <div style={{display:'flex',gap:8,marginBottom:12}}>
                    <Select allowClear placeholder="All Programmes" style={{width:220}}
                        value={filterProg} onChange={setFilterProg}>
                        {programs.map(p=><Option key={p.id} value={p.id}>{p.name}</Option>)}
                    </Select>
                </div>
                )}
                <Table rowKey="id" dataSource={surveys.filter(s=>!filterProg||String(s.program_id)===String(filterProg))} columns={cols}
                    loading={loading} size="small" pagination={{pageSize:20}}/>
            </div>

            {/* Survey Settings Modal */}
            <Modal title={current ? 'Edit Survey' : 'New Survey'} open={surveyModal}
                onCancel={()=>setSurveyModal(false)} onOk={saveSurvey} width={600}
                okText={current ? 'Save' : 'Save & Build Questions'}>
                <Form form={form} layout="vertical">
                    <Form.Item name="title" label="Survey Title" rules={[{required:true}]}>
                        <Input placeholder="e.g. Facility Readiness Assessment 2026" />
                    </Form.Item>
                    <Form.Item name="description" label="Description">
                        <TextArea rows={2} placeholder="Brief description of the survey purpose" />
                    </Form.Item>
                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item name="program_id" label={isProgramUser ? 'MoH Programme' : 'MoH Programme (optional)'}>
                                <Select allowClear={!isProgramUser} disabled={isProgramUser} placeholder="Link to a programme">
                                    {programs.map(p=><Option key={p.id} value={parseInt(p.id)}>{p.name}</Option>)}
                                </Select>
                            </Form.Item>
                    <Form.Item name="category" label="Category">
                                <Select placeholder="Select category">
                                    {['NHSSP','Patient Satisfaction','M&E Assessment','Facility Audit',
                                      'Staff Survey','Community Survey','Program Evaluation','Other']
                                    .map(c=><Option key={c} value={c}>{c}</Option>)}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="target_type" label="Target Audience">
                                <Select
                                    defaultValue="all"
                                    onChange={(v) => {
                                        setTargetType(v);
                                        // Clear a stale target_value when the audience type changes
                                        // (backend stores the selected facility/region/site-type here)
                                        form.setFieldsValue({ target_value: undefined });
                                    }}
                                >
                                    <Option value="all">All Facilities</Option>
                                    <Option value="region">By Region</Option>
                                    <Option value="site_type">By Facility Type</Option>
                                    <Option value="facility">Specific Facility</Option>
                                </Select>
                            </Form.Item>

                            {targetType === 'facility' && (
                                <Form.Item
                                    name="target_value"
                                    label="Facility"
                                    rules={[{ required: true, message: 'Select a facility' }]}
                                >
                                    <Select
                                        showSearch
                                        allowClear
                                        placeholder="Select facility..."
                                        optionFilterProp="children"
                                        filterOption={(input, option) => {
                                            // option.children can be a string, number, array of nodes,
                                            // or undefined depending on antd version/usage — never assume
                                            // it's a plain string before calling .toLowerCase() on it.
                                            const label = String(option?.children ?? '');
                                            return label.toLowerCase().includes(String(input ?? '').toLowerCase());
                                        }}
                                    >
                                        {facilities.map(f => (
                                            <Option key={f.id} value={String(f.id)}>
                                                {`${f.name}${f.region ? ` — ${f.region}` : ''}`}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            )}
                        </Col>
                    </Row>
                    <Form.Item name="period" label="Collection Period">
                        <DatePicker.RangePicker style={{width:'100%'}} />
                    </Form.Item>
                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item name="allow_multiple" label="Allow Multiple Submissions" valuePropName="checked">
                                <Switch />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="is_anonymous" label="Anonymous Responses" valuePropName="checked">
                                <Switch />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>

            {/* Question Builder Modal */}
            <Modal title={`Build Questions — ${current?.title||''}`}
                open={builderModal} onCancel={()=>setBuilderModal(false)}
                onOk={saveQuestions} width={900} okText="Save Questions"
                style={{top:20}} bodyStyle={{maxHeight:'75vh',overflowY:'auto'}}>

                {sections.map(sec => (
                    <div key={sec.id} style={{marginBottom:24}}>
                        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12,
                            borderBottom:'2px solid #1890ff',paddingBottom:8}}>
                            <Input value={sec.title} style={{fontWeight:700,fontSize:14,border:'none',padding:0,background:'transparent'}}
                                onChange={e=>setSections(ss=>ss.map(s=>s.id===sec.id?{...s,title:e.target.value}:s))}/>
                            <Button size="small" type="primary" icon={<PlusOutlined/>}
                                onClick={()=>addQuestion(sec.id)}>Add Question</Button>
                        </div>

                        {questions.filter(q=>String(q.section_id)===String(sec.id)||(!q.section_id&&sections.indexOf(sec)===0)).map((q,qi)=>(
                            <Card key={q.id} size="small" style={{marginBottom:10,borderLeft:'3px solid #1890ff'}}>
                                <Row gutter={8} align="middle">
                                    <Col span={1}>
                                        <span style={{color:'#ccc',fontWeight:700}}>{qi+1}</span>
                                    </Col>
                                    <Col span={5}>
                                        <Select value={q.type || 'text'} size="small" style={{width:'100%'}}
                                            onChange={v=>{
                                                setQuestions(qs => qs.map(qq => {
                                                    if (qq.id !== q.id) return qq;
                                                    const updated = {...qq, type: v};
                                                    if (v==='matrix') {
                                                        updated.rows = qq.rows?.length ? qq.rows : [''];
                                                        updated.columns = qq.columns?.length ? qq.columns : ['Yes','No'];
                                                    }
                                                    if (['single','multiple','dropdown'].includes(v) && !(qq.options||[]).length) {
                                                        updated.options = ['Option 1','Option 2'];
                                                    }
                                                    return updated;
                                                }));
                                            }}>
                                            {QUESTION_TYPES.map(t=>(
                                                <Option key={t.value} value={t.value}>{t.icon} {t.label}</Option>
                                            ))}
                                        </Select>
                                    </Col>
                                    <Col span={14}>
                                        <Input value={q.label} size="small" placeholder="Question text..."
                                            onChange={e=>updateQuestion(q.id,'label',e.target.value)}/>
                                    </Col>
                                    <Col span={2} style={{textAlign:'center'}}>
                                        <Tooltip title="Required">
                                            <Switch size="small" checked={q.required}
                                                onChange={v=>updateQuestion(q.id,'required',v)}/>
                                        </Tooltip>
                                    </Col>
                                    <Col span={2} style={{textAlign:'right'}}>
                                        <Button size="small" danger type="text" icon={<DeleteOutlined/>}
                                            onClick={()=>removeQuestion(q.id)}/>
                                    </Col>
                                </Row>

                                {/* Options for choice questions */}
                                {['single','multiple','dropdown'].includes(q.type) && (
                                    <div style={{marginTop:8,paddingLeft:24}}>
                                        {(q.options||[]).map((opt,oi)=>(
                                            <div key={oi} style={{display:'flex',gap:6,marginBottom:4}}>
                                                <Input size="small" value={opt} style={{flex:1}}
                                                    onChange={e=>updateOption(q.id,oi,e.target.value)}
                                                    prefix={q.type==='dropdown' ? null : <span style={{color:'#ccc'}}>{oi+1}.</span>}/>
                                                <Button size="small" type="text" danger icon={<DeleteOutlined/>}
                                                    onClick={()=>removeOption(q.id,oi)}/>
                                            </div>
                                        ))}
                                        <Button size="small" type="dashed" icon={<PlusOutlined/>}
                                            onClick={()=>addOption(q.id)}>Add Option</Button>
                                    </div>
                                )}

                                {/* Matrix config — rows and columns */}
                                {q.type==='matrix' && (
                                    <div style={{marginTop:8,paddingLeft:24}}>
                                        <div style={{fontSize:12,color:'#888',marginBottom:6}}>Rows (sub-questions):</div>
                                        {(q.rows||[]).map((row,ri)=>(
                                            <div key={ri} style={{display:'flex',gap:6,marginBottom:4}}>
                                                <Input size="small" value={row} style={{flex:1}}
                                                    onChange={e=>{const rows=[...(q.rows||[])];rows[ri]=e.target.value;updateQuestion(q.id,'rows',rows);}}
                                                    prefix={<span style={{color:'#ccc'}}>{String.fromCharCode(65+ri)}.</span>}/>
                                                <Button size="small" type="text" danger icon={<DeleteOutlined/>}
                                                    onClick={()=>updateQuestion(q.id,'rows',(q.rows||[]).filter((_,i)=>i!==ri))}/>
                                            </div>
                                        ))}
                                        <Button size="small" type="dashed" icon={<PlusOutlined/>}
                                            onClick={()=>updateQuestion(q.id,'rows',[...(q.rows||[]),''])}>Add Row</Button>
                                        <div style={{fontSize:12,color:'#888',margin:'10px 0 6px'}}>Columns (options):</div>
                                        {(q.columns||['Yes','No']).map((col,ci)=>(
                                            <div key={ci} style={{display:'flex',gap:6,marginBottom:4}}>
                                                <Input size="small" value={col} style={{flex:1}}
                                                    onChange={e=>{const cols=[...(q.columns||['Yes','No'])];cols[ci]=e.target.value;updateQuestion(q.id,'columns',cols);}}/>
                                                <Button size="small" type="text" danger icon={<DeleteOutlined/>}
                                                    onClick={()=>updateQuestion(q.id,'columns',(q.columns||[]).filter((_,i)=>i!==ci))}/>
                                            </div>
                                        ))}
                                        <Button size="small" type="dashed" icon={<PlusOutlined/>}
                                            onClick={()=>updateQuestion(q.id,'columns',[...(q.columns||['Yes','No']),''])}>Add Column</Button>
                                    </div>
                                )}

                                {/* Scale config */}
                                {q.type==='scale' && (
                                    <div style={{marginTop:8,paddingLeft:24,display:'flex',gap:12,alignItems:'center'}}>
                                        <span style={{fontSize:12,color:'#888'}}>Scale:</span>
                                        <Select size="small" value={q.scale_min} style={{width:70}}
                                            onChange={v=>updateQuestion(q.id,'scale_min',v)}>
                                            {[1,0].map(v=><Option key={v} value={v}>{v}</Option>)}
                                        </Select>
                                        <span>to</span>
                                        <Select size="small" value={q.scale_max} style={{width:70}}
                                            onChange={v=>updateQuestion(q.id,'scale_max',v)}>
                                            {[3,5,7,10].map(v=><Option key={v} value={v}>{v}</Option>)}
                                        </Select>
                                    </div>
                                )}

                                {/* Helper text */}
                                <Input size="small" value={q.helper_text} placeholder="Helper text (optional)..."
                                    style={{marginTop:6,fontSize:11,color:'#888'}}
                                    onChange={e=>updateQuestion(q.id,'helper_text',e.target.value)}/>

                                {/* Skip Logic */}
                                <div style={{marginTop:6,padding:'8px 10px',background:'#f0f9ff',borderRadius:6,border:'1px solid #bae6fd'}}>
                                    <div style={{fontSize:11,color:'#0369a1',marginBottom:5,fontWeight:600}}>⚡ Skip Logic — Show only if:</div>
                                    <div style={{display:'flex',gap:4,alignItems:'center',flexWrap:'wrap'}}>
                                        <Select size="small" style={{flex:2,minWidth:120}} placeholder="Question..." allowClear
                                            value={q.skip_logic?.field||undefined}
                                            onChange={v=>updateQuestion(q.id,'skip_logic',v?{...(q.skip_logic||{}),field:v}:null)}>
                                            {questions.filter(qq=>qq.id!==q.id).map(qq=>(
                                                <Option key={qq.id} value={qq.xlsform_name||String(qq.id)}>{(qq.label||'').slice(0,25)||qq.id}</Option>
                                            ))}
                                        </Select>
                                        <Select size="small" style={{width:80}} value={q.skip_logic?.operator||'='}
                                            onChange={v=>updateQuestion(q.id,'skip_logic',{...(q.skip_logic||{}),operator:v})}>
                                            <Option value="=">= equals</Option>
                                            <Option value="!=">≠ not</Option>
                                            <Option value="contains">contains</Option>
                                        </Select>
                                        <Input size="small" style={{flex:1,minWidth:70}} placeholder="value..."
                                            value={q.skip_logic?.value||''}
                                            onChange={e=>updateQuestion(q.id,'skip_logic',{...(q.skip_logic||{}),value:e.target.value})}/>
                                        {q.skip_logic?.field&&<Button size="small" type="text" danger onClick={()=>updateQuestion(q.id,'skip_logic',null)}>✕</Button>}
                                    </div>
                                    {q.skip_logic?.field&&<div style={{fontSize:10,color:'#1890ff',marginTop:4}}>Show when [{q.skip_logic.field}] {q.skip_logic.operator||'='} "{q.skip_logic.value}"</div>}
                                </div>
                            </Card>
                        ))}

                        {questions.filter(q=>q.section_id===sec.id).length===0 && (
                            <div style={{textAlign:'center',padding:'20px',color:'#ccc',border:'1px dashed #e5e7eb',borderRadius:8}}>
                                No questions yet. Click "Add Question" to start.
                            </div>
                        )}
                    </div>
                ))}

                <Button type="dashed" block icon={<PlusOutlined/>}
                    onClick={()=>setSections(ss=>[...ss,{id:'s_'+Date.now(),title:'New Section',description:''}])}>
                    Add Section
                </Button>
            </Modal>

            {/* XLSForm Import Modal */}
            <Modal title="Import XLSForm" open={importModal}
                onCancel={()=>setImportModal(false)} footer={null} width={820} destroyOnClose>
                <QoCSurveyImport
                    onClose={()=>setImportModal(false)}
                    onImport={async (data)=>{
                        util.showLoader();
                        try {
                            const existing = surveys.find(s => s.title === data.title);
                            let surveyId;
                            if (existing) {
                                const confirmed = window.confirm(
                                    `A survey named "${data.title}" already exists. Replace its questions with the imported ones?`
                                );
                                if (!confirmed) { util.hideLoader(); return; }
                                surveyId = existing.id;
                            } else {
                                const res = await SurveyService.saveSurvey({ title: data.title, category: 'Program Evaluation' });
                                surveyId = res.data?.data?.id;
                                if (!surveyId) throw new Error('Failed to create survey');
                            }
                            await SurveyService.saveQuestions(surveyId, { sections: data.sections, questions: data.questions });
                            message.success(`${existing ? 'Updated' : 'Imported'} "${data.title}" — ${data.questions.length} questions`);
                            setImportModal(false);
                            load();
                        } catch(e) { message.error(e.message); }
                        finally { util.hideLoader(); }
                    }}
                />
            </Modal>

            {/* Progress Modal */}
            <Modal title={`Collection Progress — ${current?.title||''}`}
                open={progressModal} onCancel={()=>setProgressModal(false)}
                footer={null} width={900} destroyOnClose>
                {current && <QoCSurveyProgress survey={current} onClose={()=>setProgressModal(false)}/>}
            </Modal>

            {/* Collectors Modal */}
            <Modal title={`Data Collectors — ${current?.title||''}`}
                open={collectorsModal} onCancel={()=>setCollectorsModal(false)}
                footer={null} width={800} destroyOnClose>
                {current && <QoCSurveyCollectors survey={current} onClose={()=>setCollectorsModal(false)}/>}
            </Modal>

            {/* Public Link Modal */}
            <Modal title="Public Survey Link" open={linkModal}
                onCancel={()=>setLinkModal(false)} footer={null} width={540}>
                <div style={{padding:'8px 0'}}>
                    <Alert type="info" showIcon style={{marginBottom:16}}
                        message="Share this link with data collectors — no login required"
                        description="Anyone with this link can submit the survey. They will select their facility on the form."/>
                    <div style={{display:'flex',gap:8,marginBottom:16}}>
                        <Input value={publicLink} readOnly style={{fontFamily:'monospace',fontSize:12}}/>
                        <Button type="primary" onClick={copyLink}>Copy</Button>
                    </div>
                    <div style={{display:'flex',gap:8,justifyContent:'center'}}>
                        <Button href={publicLink} target="_blank">Preview Form</Button>
                        <Button danger onClick={revokeLink}>Revoke Link</Button>
                    </div>
                    <div style={{marginTop:12,textAlign:'center'}}>
                        <div style={{fontSize:12,color:'#888',marginTop:8}}>📋 Copy link above to share</div>
                        <div style={{fontSize:11,color:'#888',marginTop:4}}>Scan QR code to open survey</div>
                    </div>
                </div>
            </Modal>

            {/* Collaborators Modal */}
            <Modal title='Collaborators'
                open={collabModal} onCancel={()=>setCollabModal(false)}
                footer={null} width={700} destroyOnClose>
                {current && <QoCSurveyCollaborators survey={current} onClose={()=>setCollabModal(false)}/>}
            </Modal>

            {/* Analytics Modal */}
            <Modal title={`Analytics — ${current?.title||''}`}
                open={analyticsModal} onCancel={()=>setAnalyticsModal(false)}
                footer={null} width={800} style={{top:20}}
                bodyStyle={{maxHeight:'80vh',overflowY:'auto'}}>
                {analytics && (
                    <div>
                        <Row gutter={12} style={{marginBottom:20}}>
                            <Col span={8}><Card size="small"><Statistic title="Total Responses" value={analytics.summary.total_responses} valueStyle={{color:'#1890ff',fontWeight:800}}/></Card></Col>
                            <Col span={8}><Card size="small"><Statistic title="Facilities" value={analytics.summary.total_facilities} valueStyle={{color:'#16a34a',fontWeight:800}}/></Card></Col>
                            <Col span={8}><Card size="small"><Statistic title="Questions" value={analytics.survey.questions?.length||0} valueStyle={{color:'#7c3aed',fontWeight:800}}/></Card></Col>
                        </Row>

                        {(analytics.by_question||[]).map((q,i)=>(
                            <Card key={q.id} size="small" style={{marginBottom:12}}>
                                <div style={{fontWeight:600,marginBottom:8}}>{i+1}. {q.label}</div>
                                {['single','multiple','boolean','dropdown'].includes(q.type) && (
                                    <div>
                                        {Object.entries(q.summary||{}).map(([opt,cnt])=>(
                                            <div key={opt} style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                                                <span style={{fontSize:12,minWidth:120}}>{opt}</span>
                                                <Progress percent={Math.round(cnt/Math.max(analytics.summary.total_responses,1)*100)}
                                                    size="small" style={{flex:1}} />
                                                <span style={{fontSize:11,color:'#888'}}>{cnt}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {['scale','number'].includes(q.type) && q.summary?.mean !== null && (
                                    <div style={{display:'flex',gap:24}}>
                                        <Statistic title="Mean" value={q.summary.mean} precision={2} valueStyle={{fontSize:20,color:'#1890ff'}}/>
                                        <Statistic title="Min" value={q.summary.min} valueStyle={{fontSize:20}}/>
                                        <Statistic title="Max" value={q.summary.max} valueStyle={{fontSize:20}}/>
                                    </div>
                                )}
                                {['text','textarea'].includes(q.type) && (
                                    <div style={{color:'#888',fontSize:12}}>{q.response_count} text responses</div>
                                )}
                                {q.type==='matrix' && (
                                    <div>
                                        {Object.entries(q.summary||{}).map(([row,cols])=>(
                                            <div key={row} style={{marginBottom:8}}>
                                                <div style={{fontWeight:600,fontSize:12,marginBottom:4}}>{row}</div>
                                                {Object.entries(cols||{}).map(([col,cnt])=>(
                                                    <div key={col} style={{display:'flex',alignItems:'center',gap:8,marginBottom:2}}>
                                                        <span style={{fontSize:12,minWidth:60}}>{col}</span>
                                                        <Progress percent={Math.round(cnt/Math.max(q.response_count,1)*100)} size='small' style={{flex:1}}/>
                                                        <span style={{fontSize:11,color:'#888'}}>{cnt}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Card>
                        ))}

                        {analytics.by_facility?.length > 0 && (
                            <div>
                                <Divider>Responses by Facility</Divider>
                                <Table size="small" dataSource={analytics.by_facility} rowKey="facility_name"
                                    pagination={false}
                                    columns={[
                                        {title:'Facility', dataIndex:'facility_name'},
                                        {title:'Region', dataIndex:'region', width:120},
                                        {title:'Submissions', dataIndex:'submissions', width:100, render:v=><span style={{fontWeight:700,color:'#1890ff'}}>{v}</span>},
                                        {title:'Last Submitted', dataIndex:'last_submission', width:140, render:v=>v?.split(' ')[0]},
                                    ]}/>
                            </div>
                        )}
                    </div>
                )}
            </Modal>
        </div>
    );
}

